/**
 * F-parc.2b — Parc Grêle: lista de chantiers + criar. Lado admin.
 * Cada linha leva ao detalhe (grade de forfaits + veículos).
 */

import { useState } from "react";
import { Link } from "wouter";
import { ErpShell } from "../components/layout/ErpShell.js";
import { useAuth } from "../hooks/useAuth.js";
import { useCanEdit } from "../hooks/useCanEdit.js";
import { useSelectedCompany } from "../hooks/useSelectedCompany.js";
import { useT } from "../i18n/index.js";
import { trpc } from "../lib/trpc.js";

const inputClass =
  "w-full rounded-md border border-neutral-800 bg-neutral-900 px-3 py-1.5 text-sm focus:border-lime outline-none disabled:opacity-50";

export default function HailParcs() {
  const { user } = useAuth();
  const canEdit = useCanEdit();
  const companiesQuery = trpc.companies.list.useQuery();
  const accessibleCompanies = (companiesQuery.data ?? []).filter((c) =>
    (user?.allowedCompanyIds ?? []).includes(c.id),
  );
  const [companyId, setCompanyId] = useSelectedCompany(accessibleCompanies);
  const [showCreate, setShowCreate] = useState(false);
  const t = useT();

  const listQuery = trpc.hail.parcs.list.useQuery(
    { companyId: companyId ?? 0 },
    { enabled: companyId !== null },
  );
  const utils = trpc.useUtils();

  if (!user) return null;

  return (
    <ErpShell>
      <main className="max-w-6xl mx-auto p-6 space-y-4">
        <div className="ph">
          <div>
            <h1>{t("hailParcs.title")}</h1>
            <p>{t("hailParcs.subtitle")}</p>
          </div>
          <div className="ph__actions flex items-end gap-3">
            {accessibleCompanies.length > 1 && (
              <select
                value={companyId ?? ""}
                onChange={(e) => setCompanyId(Number(e.target.value))}
                className={inputClass + " w-56"}
              >
                {accessibleCompanies.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            )}
            {canEdit && (
              <button
                onClick={() => setShowCreate(true)}
                className="rounded-md bg-lime hover:bg-lime-bright text-on-lime px-4 py-1.5 text-sm font-medium"
              >
                {t("hailParcs.newBtn")}
              </button>
            )}
          </div>
        </div>

        <div className="rounded-lg border border-neutral-800 bg-neutral-900 overflow-hidden">
          {listQuery.isLoading && (
            <p className="p-4 text-sm text-neutral-500">{t("hailParcs.loading")}</p>
          )}
          {listQuery.error && <p className="p-4 text-sm text-red-400">{listQuery.error.message}</p>}
          {listQuery.data && listQuery.data.length === 0 && (
            <p className="p-4 text-sm text-neutral-500">{t("hailParcs.emptyState")}</p>
          )}
          {listQuery.data && listQuery.data.length > 0 && (
            <table className="w-full text-sm">
              <thead className="bg-neutral-950/50 text-xs uppercase tracking-wider text-neutral-500">
                <tr>
                  <th className="text-left px-3 py-2">{t("hailParcs.colName")}</th>
                  <th className="text-left px-3 py-2">{t("hailParcs.colCarrosserie")}</th>
                  <th className="text-left px-3 py-2 w-32">{t("hailParcs.colDate")}</th>
                  <th className="text-left px-3 py-2 w-24">{t("hailParcs.colStatus")}</th>
                  <th className="px-3 py-2 w-20 text-right">{t("hailParcs.colActions")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-800">
                {listQuery.data.map((p) => (
                  <tr key={p.id}>
                    <td className="px-3 py-2 font-medium">
                      <Link href={`/parc-grele/${p.id}`} className="text-lime hover:underline">
                        {p.name}
                      </Link>
                    </td>
                    <td className="px-3 py-2 text-neutral-300">{p.customerName ?? "—"}</td>
                    <td className="px-3 py-2 text-xs text-neutral-400">{p.eventDate ?? "—"}</td>
                    <td className="px-3 py-2">
                      {p.status === "open" ? (
                        <span className="inline-block rounded bg-lime/20 text-lime px-2 py-0.5 text-xs">
                          {t("hailParcs.statusOpen")}
                        </span>
                      ) : (
                        <span className="inline-block rounded bg-neutral-700/40 text-neutral-400 px-2 py-0.5 text-xs">
                          {t("hailParcs.statusClosed")}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Link
                        href={`/parc-grele/${p.id}`}
                        className="text-xs text-lime hover:underline"
                      >
                        {t("hailParcs.openLink")}
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {showCreate && companyId !== null && (
          <CreateParcModal
            companyId={companyId}
            onClose={() => setShowCreate(false)}
            onSaved={() => {
              setShowCreate(false);
              utils.hail.parcs.list.invalidate();
            }}
          />
        )}
      </main>
    </ErpShell>
  );
}

function CreateParcModal({
  companyId,
  onClose,
  onSaved,
}: {
  companyId: number;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState("");
  const [customerId, setCustomerId] = useState<number | "">("");
  const [eventDate, setEventDate] = useState("");
  const [error, setError] = useState<string | null>(null);

  const customersQuery = trpc.customers.list.useQuery({
    companyId,
    active: true,
    limit: 200,
    offset: 0,
  });
  const createMutation = trpc.hail.parcs.create.useMutation({
    onSuccess: onSaved,
    onError: (e) => setError(e.message),
  });
  const t = useT();

  function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!name.trim() || customerId === "") {
      setError(t("hailParcs.validationNameCustomer"));
      return;
    }
    createMutation.mutate({
      companyId,
      customerId: Number(customerId),
      name: name.trim(),
      eventDate: eventDate ? new Date(eventDate) : undefined,
    });
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
      <div className="w-full max-w-md bg-neutral-950 border border-neutral-800 rounded-lg shadow-xl">
        <div className="px-5 py-4 border-b border-neutral-800 flex items-center justify-between">
          <h2 className="text-lg font-semibold">{t("hailParcs.createModalTitle")}</h2>
          <button onClick={onClose} className="text-neutral-500 hover:text-neutral-300 text-lg">
            ×
          </button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <div>
            <label className="block text-xs text-neutral-400 mb-1">
              {t("hailParcs.nameLabel")} <span className="text-red-400">*</span>
            </label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={200}
              required
              placeholder={t("hailParcs.namePlaceholder")}
              className={inputClass}
            />
          </div>
          <div>
            <label className="block text-xs text-neutral-400 mb-1">
              {t("hailParcs.customerLabel")} <span className="text-red-400">*</span>
            </label>
            <select
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value ? Number(e.target.value) : "")}
              required
              className={inputClass}
            >
              <option value="">{t("hailParcs.selectPlaceholder")}</option>
              {(customersQuery.data?.rows ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.displayName}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-neutral-400 mb-1">
              {t("hailParcs.eventDateLabel")}
            </label>
            <input
              type="date"
              value={eventDate}
              onChange={(e) => setEventDate(e.target.value)}
              className={inputClass}
            />
          </div>

          {error && (
            <div className="text-red-400 text-sm bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t border-neutral-800">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-neutral-800 hover:border-neutral-700 px-4 py-1.5 text-sm"
            >
              {t("hailParcs.cancel")}
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="rounded-md bg-lime hover:bg-lime-bright text-on-lime disabled:opacity-50 px-4 py-1.5 text-sm font-medium"
            >
              {createMutation.isPending ? t("hailParcs.creating") : t("hailParcs.create")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
